// src/Component/SideData/Overview/AiAgentWorkFlow/ChainApis.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import styles from "./ChainApis.module.css";
import shared from "./shared.module.css";
import { RiDeleteBin6Line } from "react-icons/ri";
import {
  BiSolidEdit,
  BiSolidToggleLeft,
  BiSolidToggleRight,
  BiSolidGrid,
  BiSolidLayout,
} from "react-icons/bi";
import {
  FaAngleDown,
  FaChevronRight,
  FaChevronDown,
  FaHandPaper,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaLink,
  FaUnlink,
  FaExclamationTriangle,
  FaSyncAlt,
} from "react-icons/fa";
import StepMappingEditor from "./StepMappingEditor";
import CanvasView from "./CanvasView";
import ReusablePopup from "../../../../ReusablePopup";

import {
  TriggerAttachmentList,
} from "./TriggerAttachFlow";
import TriggerAttachFlow from "./TriggerAttachFlow";
import { siteFieldsFromChainNode } from "./siteFields";
import { normaliseTriggerList } from "./triggerAttachment";

import { listOperations } from "./utils/operationsApi";
import { listDefinitions } from "./utils/connectorDefinitionsApi";
import { listConnections } from "./utils/oauthApi";

import ScopeTabs from "./ScopeTabs";
import ResourceScopeBadge from "./utils/ResourceScopeBadge";
import {
  attachScope,
  detachScope,
  listAttachments,
  getAttachmentsByResource,
} from "./utils/scopeAttachmentsApi";
import {
  classifyGuardRefusal,
  formatAttachedAppChips,
  formatResourceInUseError,
} from "./utils/scopeAttachmentValidation";

const S = { ...styles, ...shared };

const SCOPE = {
  APP: "app",
  TENANT: "tenant",
};

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const defaultChainNode = (api_config_id = "") => ({
  api_config_id,
  manual: false,
  input_mapping: {},
  path_param_mappings: {},
  query_param_mappings: {},
  payload_param_mappings: {},
  post_triggers: [],
  branches: {},
});

const defaultChain = {
  name: "",
  root: defaultChainNode(""),
  enabled: true,
};

function updateNodeAtPath(root, nodePath, updater) {
  if (nodePath.length === 0) return updater(root);
  const [head, ...rest] = nodePath;
  const current = root.branches?.[head] || defaultChainNode("");
  return {
    ...root,
    branches: {
      ...(root.branches || {}),
      [head]: updateNodeAtPath(current, rest, updater),
    },
  };
}

function getNodeAtPath(root, nodePath) {
  let node = root;
  for (const key of nodePath) {
    node = node?.branches?.[key];
    if (!node) return null;
  }
  return node;
}

function removeBranchAtPath(root, nodePath, statusCode) {
  return updateNodeAtPath(root, nodePath, (node) => {
    const nextBranches = { ...(node.branches || {}) };
    delete nextBranches[statusCode];
    return { ...node, branches: nextBranches };
  });
}

function countBranchesRecursive(node) {
  const branches = node?.branches || {};
  let total = Object.keys(branches).length;
  for (const child of Object.values(branches)) {
    total += countBranchesRecursive(child);
  }
  return total;
}

function maxDepth(node) {
  const branches = node?.branches || {};
  const keys = Object.keys(branches);
  if (keys.length === 0) return 0;
  let best = 0;
  for (const child of Object.values(branches)) {
    best = Math.max(best, maxDepth(child));
  }
  return best + 1;
}

function sortBranchKeys(keys) {
  return [...keys].sort((a, b) => {
    const aNum = a === "default" ? Number.POSITIVE_INFINITY : parseInt(a, 10);
    const bNum = b === "default" ? Number.POSITIVE_INFINITY : parseInt(b, 10);
    if (Number.isNaN(aNum) || Number.isNaN(bNum)) return a.localeCompare(b);
    return aNum - bNum;
  });
}

function pruneEmptyBranches(node) {
  if (!node || typeof node !== "object") return node;
  const prunedBranches = {};
  for (const [status, child] of Object.entries(node.branches || {})) {
    const prunedChild = pruneEmptyBranches(child);
    const hasApi = !!prunedChild.api_config_id;
    const hasChildren = Object.keys(prunedChild.branches || {}).length > 0;
    if (hasApi || hasChildren) {
      prunedBranches[status] = prunedChild;
    }
  }
  return { ...node, branches: prunedBranches };
}

function normalizeChainNode(node) {
  if (!node || typeof node !== "object") return null;
  return {
    api_config_id: node.api_config_id || "",
    manual: !!node.manual,
    input_mapping: node.input_mapping || {},
    path_param_mappings: node.path_param_mappings || {},
    query_param_mappings: node.query_param_mappings || {},
    payload_param_mappings: node.payload_param_mappings || {},
    post_triggers: normaliseTriggerList(node.post_triggers),
    branches: Object.fromEntries(
      Object.entries(node.branches || {}).map(([status, child]) => [
        status,
        normalizeChainNode(child) || defaultChainNode(""),
      ]),
    ),
  };
}

function statusClass(status) {
  if (status === "default") return "sDefault";
  const n = parseInt(status, 10);
  if (Number.isNaN(n)) return "sDefault";
  if (n >= 200 && n < 300) return "s2xx";
  if (n >= 300 && n < 400) return "s3xx";
  if (n >= 400 && n < 500) return "s4xx";
  if (n >= 500) return "s5xx";
  return "sDefault";
}

function analyzeChain(root) {
  const perNodeStage = {};
  let totalNodes = 0;
  let nodesWithApi = 0;
  let totalEdges = 0;
  let edgesMapped = 0;

  const visit = (node, nodePath) => {
    if (!node) return;
    totalNodes += 1;
    const key = nodePath.join("/") || "root";
    const hasApi = !!node.api_config_id;
    if (hasApi) nodesWithApi += 1;

    const childKeys = Object.keys(node.branches || {});
    let nodeStage;

    if (!hasApi) {
      nodeStage = "empty";
    } else {
      let anyChildMissingApi = false;
      let anyChildMissingMapping = false;

      for (const status of childKeys) {
        const child = node.branches[status];
        const childHasApi = !!child?.api_config_id;
        if (!childHasApi) {
          anyChildMissingApi = true;
          continue;
        }
        totalEdges += 1;
        const mappingEntries =
          Object.keys(child.path_param_mappings || {}).length +
          Object.keys(child.query_param_mappings || {}).length +
          Object.keys(child.payload_param_mappings || {}).length +
          Object.keys(child.input_mapping || {}).length;
        if (mappingEntries > 0) {
          edgesMapped += 1;
        } else {
          anyChildMissingMapping = true;
        }
      }

      if (childKeys.length === 0) {
        nodeStage = "done";
      } else if (anyChildMissingApi || anyChildMissingMapping) {
        nodeStage = "needsMap";
      } else {
        nodeStage = "done";
      }
    }

    perNodeStage[key] = nodeStage;

    for (const status of childKeys) {
      visit(node.branches[status], [...nodePath, status]);
    }
  };

  visit(root, []);
  return { perNodeStage, totalNodes, nodesWithApi, totalEdges, edgesMapped };
}

function isIncomplete(root, sel) {
  if (sel.kind === "node") {
    const node = getNodeAtPath(root, sel.nodePath);
    if (!node) return true;
    if (!node.api_config_id) return true;
    return false;
  }
  const parent = getNodeAtPath(root, sel.nodePath);
  const child = parent?.branches?.[sel.status];
  if (!child?.api_config_id) return true;
  const entries =
    Object.keys(child.path_param_mappings || {}).length +
    Object.keys(child.query_param_mappings || {}).length +
    Object.keys(child.payload_param_mappings || {}).length +
    Object.keys(child.input_mapping || {}).length;
  return entries === 0;
}

function findNextIncomplete(root, from) {
  const flatten = [];
  const collect = (node, nodePath) => {
    flatten.push({ kind: "node", nodePath });
    const childKeys = sortBranchKeys(Object.keys(node.branches || {}));
    for (const status of childKeys) {
      const child = node.branches[status];
      if (!child?.api_config_id) {
        flatten.push({ kind: "node", nodePath: [...nodePath, status] });
        continue;
      }
      const mappingEntries =
        Object.keys(child.path_param_mappings || {}).length +
        Object.keys(child.query_param_mappings || {}).length +
        Object.keys(child.payload_param_mappings || {}).length +
        Object.keys(child.input_mapping || {}).length;
      if (mappingEntries === 0) {
        flatten.push({ kind: "edge", nodePath, status });
      }
      collect(child, [...nodePath, status]);
    }
  };
  collect(root, []);

  const fromKey =
    from.kind === "edge"
      ? `edge:${from.nodePath.join("/")}:${from.status}`
      : `node:${from.nodePath.join("/")}`;
  const fromIdx = flatten.findIndex((s) =>
    s.kind === "edge"
      ? `edge:${s.nodePath.join("/")}:${s.status}` === fromKey
      : `node:${s.nodePath.join("/")}` === fromKey,
  );
  const start = fromIdx >= 0 ? fromIdx + 1 : 0;

  for (let i = start; i < flatten.length; i++) {
    if (isIncomplete(root, flatten[i])) return flatten[i];
  }
  for (let i = 0; i < start; i++) {
    if (isIncomplete(root, flatten[i])) return flatten[i];
  }
  return null;
}

export default function ChainApis({ appData }) {
  const region = appData?.region?.toLowerCase();
  const token = localStorage.getItem("token") || "";
  const appName = appData?.app_name;

  const isGlobalContext = !appName;

  const [activeTab, setActiveTab] = useState(SCOPE.TENANT);

  const [tenantChains, setTenantChains] = useState([]);
  const [appChains, setAppChains] = useState([]);
  const [attachedIds, setAttachedIds] = useState(() => new Set());
  const [attachmentsByChain, setAttachmentsByChain] = useState(
    () => new Map(),
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("list");

  const [showForm, setShowForm] = useState(false);
  const [editingChainId, setEditingChainId] = useState(null);
  const [form, setForm] = useState(defaultChain);

  const [apiConfigs, setApiConfigs] = useState([]);

  const [selection, setSelection] = useState({ kind: "node", nodePath: [] });
  const [expanded, setExpanded] = useState({});

  const [operations, setOperations] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);

  const [connectorDefinitions, setConnectorDefinitions] = useState([]);
  const [loadingDefinitions, setLoadingDefinitions] = useState(false);

  const [connectedProviders, setConnectedProviders] = useState([]);

  const [attachFlowOpen, setAttachFlowOpen] = useState(false);
  const [attachFlowTargetNodePath, setAttachFlowTargetNodePath] = useState(null);

  const [popup, setPopup] = useState({
    isOpen: false,
    variant: "confirm",
    heading: "",
    subtext: "",
    paragraph: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    icon: null,
    iconColor: "#dc2626",
    iconBG: "#fee2e2",
    onConfirm: null,
    onCancel: null,
  });

  const closePopup = () => {
    setPopup((p) => {
      if (p.onCancel) p.onCancel();
      return { ...p, isOpen: false, onConfirm: null, onCancel: null };
    });
  };

  const openConfirmPopup = ({
    heading,
    subtext = "",
    paragraph = "",
    confirmText = "Confirm",
    cancelText = "Cancel",
    icon = null,
    iconColor = "#dc2626",
    iconBG = "#fee2e2",
    onConfirm,
  }) => {
    setPopup({
      isOpen: true,
      variant: "confirm",
      heading,
      subtext,
      paragraph,
      confirmText,
      cancelText,
      icon,
      iconColor,
      iconBG,
      onConfirm,
      onCancel: null,
    });
  };

  const openAlert = ({
    heading = "",
    subtext = "",
    paragraph = "",
    confirmText = "OK",
    icon = null,
    iconColor = "#16a34a",
    iconBG = "#dcfce7",
  }) => {
    setPopup({
      isOpen: true,
      variant: "alert",
      heading,
      subtext,
      paragraph,
      confirmText,
      cancelText: "",
      icon,
      iconColor,
      iconBG,
      onConfirm: () => {
        setPopup((p) => ({
          ...p,
          isOpen: false,
          onConfirm: null,
          onCancel: null,
        }));
      },
      onCancel: null,
    });
  };

  const openGuardPopup = ({ refusal, onForce }) => {
    const BodyNode = (
      <div style={{ textAlign: "left", lineHeight: 1.6 }}>
        {refusal.kind === "attached-in-use" && (
          <>
            <div style={{ marginBottom: 8 }}>{refusal.message}</div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 6,
              }}
            >
              {formatAttachedAppChips(
                refusal.apps.map((a) => ({ app_name: a })),
              ).map((chip) => (
                <button
                  key={chip.app_name}
                  type="button"
                  onClick={async () => {
                    try {
                      await detachScope({
                        region,
                        appName: chip.app_name,
                        resourceType: "chain",
                        tenantResourceId: refusal.resourceId,
                      });
                      openAlert({
                        heading: "Detached",
                        paragraph: `${chip.app_name} has been detached from this chain.`,
                        icon: <FaCheckCircle size={28} />,
                      });
                      closePopup();
                      fetchChains();
                    } catch (e) {
                      openAlert({
                        heading: "Couldn't detach",
                        paragraph: e.message,
                        icon: <FaTimesCircle size={28} />,
                        iconColor: "#dc2626",
                        iconBG: "#fee2e2",
                      });
                    }
                  }}
                  style={{
                    padding: "4px 10px",
                    fontSize: 12,
                    borderRadius: 4,
                    border: "1px solid #c4b5fd",
                    background: "#f5f3ff",
                    color: "#6b46c1",
                    cursor: "pointer",
                  }}
                >
                  Detach {chip.app_name}
                </button>
              ))}
            </div>
            {refusal.canForce && onForce && (
              <div style={{ marginTop: 12, fontSize: 12, color: "#718096" }}>
                To update anyway, click <b>Force save</b>.
              </div>
            )}
          </>
        )}
        {refusal.kind === "referenced" && (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: "inherit",
              margin: 0,
              fontSize: 13,
            }}
          >
            {formatResourceInUseError({
              blocking_refs: refusal.refs,
              error: refusal.message,
            })}
          </pre>
        )}
        {refusal.kind !== "attached-in-use" &&
          refusal.kind !== "referenced" && <div>{refusal.message}</div>}
      </div>
    );

    setPopup({
      isOpen: true,
      variant: "confirm",
      heading:
        refusal.kind === "attached-in-use"
          ? "This chain is attached to other apps"
          : refusal.kind === "referenced"
            ? "This chain is in use"
            : "Couldn't complete the request",
      subtext: "",
      paragraph: BodyNode,
      confirmText:
        refusal.kind === "attached-in-use" && refusal.canForce
          ? "Force save"
          : "OK",
      cancelText:
        refusal.kind === "attached-in-use" && refusal.canForce
          ? "Cancel"
          : "",
      icon:
        refusal.kind === "attached-in-use" ? (
          <FaExclamationTriangle size={28} />
        ) : (
          <FaTimesCircle size={28} />
        ),
      iconColor: "#d97706",
      iconBG: "#fef3c7",
      onConfirm:
        refusal.kind === "attached-in-use" && refusal.canForce && onForce
          ? () => {
              closePopup();
              onForce();
            }
          : closePopup,
      onCancel: null,
    });
  };

  const fetchChains = useCallback(async () => {
    if (!region) return;
    setLoading(true);
    try {
      // ⭐ Two parallel calls:
      //   1. App-scoped configs (excludes tenant rows)
      //   2. ALL tenant-scoped configs (regardless of attachment)
      const appParams = new URLSearchParams();
      if (appName) appParams.set("app_name", appName);
      appParams.set("include_tenant", "false");

      const tenantParams = new URLSearchParams();
      tenantParams.set("scope", "tenant");
      if (appName) tenantParams.set("app_name", appName);

      const [appResp, tenantResp, attachResp] = await Promise.all([
        fetch(
          `https://${region}.autobot2.${backend_api_base_url}/chain?${appParams.toString()}`,
          { headers: { Authorization: token } },
        ),
        fetch(
          `https://${region}.autobot2.${backend_api_base_url}/chain?${tenantParams.toString()}`,
          { headers: { Authorization: token } },
        ),
        appName
          ? listAttachments({
              region,
              appName,
              resourceType: "chain",
            })
          : getAttachmentsByResource({
              region,
              resourceType: "chain",
            }).then((map) => {
              const attachments = [];
              for (const bucket of map.values()) {
                for (const a of bucket.attachments) attachments.push(a);
              }
              return { attachments, count: attachments.length };
            }),
      ]);

      if (!appResp.ok || !tenantResp.ok) {
        setError("Failed to load chains");
        return;
      }

      const appDataRes = await appResp.json();
      const tenantDataRes = await tenantResp.json();
      
      const rawApp = appDataRes.chains || [];
      const rawTenant = tenantDataRes.chains || [];

      const appOwned = rawApp.filter((c) => c.scope === SCOPE.APP);
      const tenant = rawTenant.filter((c) => c.scope === SCOPE.TENANT);

      setTenantChains(tenant);
      setAppChains(appOwned);

      const ids = new Set();
      const grouped = new Map();
      for (const row of attachResp.attachments || []) {
        const rid = row.tenant_resource_id;
        if (!rid) continue;
        if (!grouped.has(rid)) {
          grouped.set(rid, { app_names: [], attachments: [] });
        }
        const bucket = grouped.get(rid);
        bucket.attachments.push(row);
        if (row.app_name && !bucket.app_names.includes(row.app_name)) {
          bucket.app_names.push(row.app_name);
        }
        if (appName && row.app_name === appName) ids.add(rid);
      }
      for (const bucket of grouped.values()) bucket.app_names.sort();

      setAttachedIds(ids);
      setAttachmentsByChain(grouped);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [region, token, appName]);

  const fetchApiConfigs = useCallback(async () => {
    if (!region) return;
    try {
      const params = new URLSearchParams();
      if (appName) params.set("app_name", appName);
      params.set("include_tenant", "true");
      const qs = params.toString();

      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/api_config?${qs}`,
        { headers: { Authorization: token } },
      );
      if (resp.ok) {
        const data = await resp.json();
        const filtered = (data.configs || []).filter(
          (cfg) => cfg.api_type === "REGULAR",
        );
        setApiConfigs(filtered);
      }
    } catch (err) {
      console.error("Failed to load API configs", err);
    }
  }, [region, token, appName]);

  useEffect(() => {
    fetchChains();
    fetchApiConfigs();
  }, [fetchChains, fetchApiConfigs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!region) return;
      setLoadingOperations(true);
      try {
        const ops = await listOperations({ region });
        if (!cancelled) setOperations(ops.operations || []);
      } catch (err) {
        if (!cancelled) {
          console.warn("[ChainApis] failed to load operations:", err);
          setOperations([]);
        }
      } finally {
        if (!cancelled) setLoadingOperations(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [region]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!region || !appName) return;
      setLoadingDefinitions(true);
      try {
        const data = await listDefinitions({ appName, region });
        if (!cancelled) setConnectorDefinitions(data.definitions || []);
      } catch (err) {
        if (!cancelled) {
          console.warn(
            "[ChainApis] failed to load connector definitions:",
            err,
          );
          setConnectorDefinitions([]);
        }
      } finally {
        if (!cancelled) setLoadingDefinitions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [region, appName]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!region || !appName) return;
      try {
        const data = await listConnections({
          appName,
          region,
        });
        if (!cancelled) {
          const providers = Array.isArray(data.connections)
            ? [...new Set(data.connections.map((c) => c.provider))]
            : [];
          setConnectedProviders(providers);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(
            "[ChainApis] failed to load connected providers:",
            err,
          );
          setConnectedProviders([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [region, appName]);

  const openCreate = () => {
    setEditingChainId(null);
    setForm(defaultChain);
    setSelection({ kind: "node", nodePath: [] });
    setExpanded({});
    setShowForm(true);
  };

  const openEdit = (chain) => {
    if (chain.scope === SCOPE.TENANT && !isGlobalContext) {
      openAlert({
        heading: "Shared chain",
        paragraph:
          "This chain is published by your tenant admin. You can attach or detach it, but only the tenant admin can edit it.",
        icon: <FaInfoCircle size={28} />,
        iconColor: "#2b6cb0",
        iconBG: "#ebf8ff",
      });
      return;
    }
    setEditingChainId(chain.chain_id);
    setForm({
      name: chain.name || "",
      root: normalizeChainNode(chain.root) || defaultChainNode(""),
      enabled: chain.enabled !== false,
    });
    setSelection({ kind: "node", nodePath: [] });
    setExpanded({});
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingChainId(null);
    setSelection({ kind: "node", nodePath: [] });
    setExpanded({});
  };

  const handleNodeApiChange = (nodePath, newApiConfigId) => {
    setForm((prev) => ({
      ...prev,
      root: updateNodeAtPath(prev.root, nodePath, (node) => {
        if (newApiConfigId === node.api_config_id) return node;

        const newCfg = apiConfigs.find(
          (c) => c.api_config_id === newApiConfigId,
        );
        const declaredStatuses = Object.keys(
          newCfg?.api_config?.sample_responses_by_status || {},
        );

        const seeded = {};
        for (const status of declaredStatuses) {
          seeded[status] = node.branches?.[status] || defaultChainNode("");
        }

        return {
          ...node,
          api_config_id: newApiConfigId,
          branches: seeded,
        };
      }),
    }));
  };

  const handleNodeManualToggle = (nodePath, checked) => {
    setForm((prev) => ({
      ...prev,
      root: updateNodeAtPath(prev.root, nodePath, (node) => ({
        ...node,
        manual: !!checked,
      })),
    }));
  };

  const handleDeleteBranch = (nodePath, statusCode) => {
    openConfirmPopup({
      heading: `Delete branch "${statusCode}"?`,
      paragraph:
        "Any sub-branches below it will also be removed. This cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: <RiDeleteBin6Line size={28} />,
      iconColor: "#dc2626",
      iconBG: "#fee2e2",
      onConfirm: () => {
        closePopup();
        setForm((prev) => ({
          ...prev,
          root: removeBranchAtPath(prev.root, nodePath, statusCode),
        }));
        if (
          selection.kind === "edge" &&
          selection.nodePath.join("/") === nodePath.join("/") &&
          selection.status === statusCode
        ) {
          setSelection({ kind: "node", nodePath });
        }
      },
    });
  };

  const handleBranchMappingChange = (
    nodePath,
    statusCode,
    key,
    newKey,
    newValue,
    fullMapping,
  ) => {
    setForm((prev) => {
      const fullPath = [...nodePath, statusCode];
      return {
        ...prev,
        root: updateNodeAtPath(prev.root, fullPath, (node) => {
          let newMapping;
          if (fullMapping) {
            newMapping = fullMapping;
          } else {
            newMapping = { ...(node.input_mapping || {}) };
            if (key && key !== newKey) delete newMapping[key];
            newMapping[newKey] = newValue;
          }
          return { ...node, input_mapping: newMapping };
        }),
      };
    });
  };

  const handleBranchParamMappingsChange = (
    nodePath,
    statusCode,
    { path, query, payload },
  ) => {
    setForm((prev) => {
      const fullPath = [...nodePath, statusCode];
      return {
        ...prev,
        root: updateNodeAtPath(prev.root, fullPath, (node) => ({
          ...node,
          path_param_mappings: path || {},
          query_param_mappings: query || {},
          payload_param_mappings: payload || {},
        })),
      };
    });
  };

  const openAttachFlow = useCallback((nodePath) => {
    setAttachFlowTargetNodePath(nodePath);
    setAttachFlowOpen(true);
  }, []);

  const closeAttachFlow = useCallback(() => {
    setAttachFlowOpen(false);
    setAttachFlowTargetNodePath(null);
  }, []);

  const handleAttachTrigger = useCallback(
    (attachment) => {
      const nodePath = attachFlowTargetNodePath;
      if (!nodePath) return;

      setForm((prev) => {
        const node = getNodeAtPath(prev.root, nodePath);
        if (!node) return prev;

        const existing = Array.isArray(node.post_triggers)
          ? node.post_triggers
          : [];
        const next = [...existing, attachment];

        return {
          ...prev,
          root: updateNodeAtPath(prev.root, nodePath, (n) => ({
            ...n,
            post_triggers: next,
          })),
        };
      });

      closeAttachFlow();
    },
    [attachFlowTargetNodePath, closeAttachFlow],
  );

  const performSaveChain = async (force = false) => {
    const prunedRoot = pruneEmptyBranches(form.root);
    const payload = {
      tenant_id: appData?.tenant_id,
      app_name: appName,
      scope: isGlobalContext ? SCOPE.TENANT : SCOPE.APP,
      ...(isGlobalContext ? {} : { app_name: appName }),
      name: form.name,
      root: prunedRoot,
      enabled: form.enabled,
    };
    if (force) payload.force = true;
    if (editingChainId) payload.chain_id = editingChainId;

    try {
      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/chain`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(payload),
        },
      );
      if (resp.ok) {
        openAlert({
          heading: "Chain saved",
          paragraph: "Chain saved successfully.",
          icon: <FaCheckCircle size={28} />,
          iconColor: "#16a34a",
          iconBG: "#dcfce7",
        });
        closeForm();
        fetchChains();
        return;
      }

      let body = {};
      try {
        body = await resp.json();
      } catch (_) {}
      const refusal = classifyGuardRefusal(body);

      if (refusal.kind === "attached-in-use") {
        openGuardPopup({
          refusal: { ...refusal, resourceId: editingChainId },
          onForce: () => performSaveChain(true),
        });
        return;
      }
      if (refusal.kind === "referenced") {
        openGuardPopup({ refusal });
        return;
      }

      openAlert({
        heading: "Failed to save",
        paragraph:
          body?.error || body?.detail || `Save failed (HTTP ${resp.status})`,
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    } catch (e) {
      openAlert({
        heading: "Error saving chain",
        paragraph: e.message || "Something went wrong. Please try again.",
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    }
  };

  const handleSave = () => performSaveChain(false);

  const performDeleteChain = async (chainId) => {
    try {
      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/chain?chain_id=${chainId}`,
        {
          method: "DELETE",
          headers: { Authorization: token },
        },
      );
      if (resp.ok) {
        openAlert({
          heading: "Chain deleted",
          paragraph: "The chain has been removed.",
          icon: <FaCheckCircle size={28} />,
          iconColor: "#16a34a",
          iconBG: "#dcfce7",
        });
        fetchChains();
        return;
      }

      let body = {};
      try {
        body = await resp.json();
      } catch (_) {}
      const refusal = classifyGuardRefusal(body);

      if (refusal.kind === "attached-in-use") {
        openGuardPopup({ refusal: { ...refusal, resourceId: chainId } });
        return;
      }
      if (refusal.kind === "referenced") {
        openGuardPopup({ refusal });
        return;
      }

      openAlert({
        heading: "Failed to delete",
        paragraph:
          body?.error || body?.detail || `Delete failed (HTTP ${resp.status})`,
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    } catch (e) {
      openAlert({
        heading: "Deletion failed",
        paragraph: e.message || "Could not delete the chain.",
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    }
  };

  const handleDelete = (chainId) => {
    openConfirmPopup({
      heading: "Delete this chain?",
      paragraph:
        "This will permanently remove the chain and all its branches. This cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: <RiDeleteBin6Line size={28} />,
      iconColor: "#dc2626",
      iconBG: "#fee2e2",
      onConfirm: () => {
        closePopup();
        performDeleteChain(chainId);
      },
    });
  };

  const handleToggle = async (chain) => {
    const newEnabled = !(chain.enabled !== false);
    const payload = {
      tenant_id: chain.tenant_id || appData?.tenant_id,
      chain_id: chain.chain_id,
      app_name: chain.app_name || appName,
      scope: chain.scope || SCOPE.APP,
      name: chain.name,
      root: chain.root,
      enabled: newEnabled,
    };
    try {
      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/chain`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(payload),
        },
      );
      if (resp.ok) fetchChains();
    } catch (e) {
      openAlert({
        heading: "Toggle failed",
        paragraph: e.message || "Could not change the chain's state.",
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    }
  };

  const handleAttachChain = async (chain) => {
    try {
      await attachScope({
        region,
        appName,
        resourceType: "chain",
        tenantResourceId: chain.chain_id,
      });
      openAlert({
        heading: "Attached",
        paragraph: "This chain is now available to this app.",
        icon: <FaCheckCircle size={28} />,
      });
      fetchChains();
    } catch (err) {
      openAlert({
        heading: "Couldn't attach",
        paragraph: err.message,
                icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    }
  };

  const handleDetachChain = (chain) => {
    openConfirmPopup({
      heading: "Detach this chain from this app?",
      subtext: `Chain: ${chain.name || chain.chain_id}`,
      paragraph:
        "The chain stays on the tenant — other apps that use it are unaffected. This app will stop seeing it, and any form or skill still mapped to it will fail on next run.",
      confirmText: "Detach",
      cancelText: "Cancel",
      icon: <FaExclamationTriangle size={28} />,
      iconColor: "#d97706",
      iconBG: "#fef3c7",
      onConfirm: async () => {
        closePopup();
        try {
          await detachScope({
            region,
            appName,
            resourceType: "chain",
            tenantResourceId: chain.chain_id,
          });
          openAlert({
            heading: "Detached",
            paragraph: "This app no longer has access to this chain.",
            icon: <FaCheckCircle size={28} />,
          });
          fetchChains();
        } catch (err) {
          openAlert({
            heading: "Couldn't detach",
            paragraph: err.message,
            icon: <FaTimesCircle size={28} />,
            iconColor: "#dc2626",
            iconBG: "#fee2e2",
          });
        }
      },
    });
  };

  const getLeadFieldNames = useCallback(
    (nodePath) => {
      const node = getNodeAtPath(form.root, nodePath);
      const rootApiConfigId = node?.api_config_id;
      if (!rootApiConfigId) return [];
      const cfg = apiConfigs.find((c) => c.api_config_id === rootApiConfigId);
      if (!cfg) return [];
      const payloadTemplate = cfg.api_config?.payload_template || "{}";
      const matches = payloadTemplate.match(/\{\{\s*(\w+)\s*\}\}/g) || [];
      return matches.map((m) => m.replace(/[\{\}\s]/g, ""));
    },
    [form.root, apiConfigs],
  );

  const siteFieldsForNode = useCallback(
    (nodePath) => {
      const node = getNodeAtPath(form.root, nodePath);
      if (!node || !node.api_config_id) return [];
      const cfg = apiConfigs.find((c) => c.api_config_id === node.api_config_id);
      const apiCfg = cfg?.api_config || {};
      const sampleMap = apiCfg.sample_responses_by_status || {};
      const first2xx = Object.keys(sampleMap).find((s) => s.startsWith("2"));
      const sampleBody = first2xx ? sampleMap[first2xx] : null;
      return siteFieldsFromChainNode(apiCfg, sampleBody);
    },
    [form.root, apiConfigs],
  );

  const pathKey = (p) => p.join("/") || "root";
  const isExpanded = (p) => expanded[pathKey(p)] !== false;
  const toggleExpanded = (p) =>
    setExpanded((prev) => ({
      ...prev,
      [pathKey(p)]: !isExpanded(p),
    }));

  const expandTo = (nodePath) => {
    const keys = {};
    for (let i = 1; i <= nodePath.length; i++) {
      keys[nodePath.slice(0, i).join("/")] = true;
    }
    setExpanded((prev) => ({ ...prev, ...keys }));
  };

  const selectedNode = useMemo(() => {
    if (selection.kind === "node") {
      return getNodeAtPath(form.root, selection.nodePath);
    }
    if (selection.kind === "edge") {
      return getNodeAtPath(form.root, selection.nodePath);
    }
    return null;
  }, [selection, form.root]);

  const analysis = useMemo(() => analyzeChain(form.root), [form.root]);

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        Loading Chains...
      </div>
    );
  }
  if (error) {
    return <div style={{ color: "red", padding: "20px" }}>Error: {error}</div>;
  }

  const renderTreeNode = (node, nodePath, stageMap) => {
    const cfg = apiConfigs.find((c) => c.api_config_id === node.api_config_id);
    const branchKeys = sortBranchKeys(Object.keys(node.branches || {}));
    const isRoot = nodePath.length === 0;
    const key = pathKey(nodePath);
    const stage = stageMap[key] || "empty";

    const isNodeSelected =
      selection.kind === "node" &&
      selection.nodePath.join("/") === nodePath.join("/");

    const isEdgeHere =
      selection.kind === "edge" &&
      selection.nodePath.join("/") === nodePath.join("/");

    const expandedHere = isExpanded(nodePath);
    const fullUrl = cfg?.api_config?.url;
    const method = cfg?.api_config?.method;

    const displayUrl = (() => {
      if (!fullUrl) return isRoot ? "Select root API" : "Select API";
      try {
        const u = new URL(fullUrl);
        return u.pathname + (u.search || "");
      } catch {
        return fullUrl;
      }
    })();

    const stageGlyph =
      { empty: "!", needsMap: "✎", done: "✓", manual: "✋" }[stage] || "•";

    const stageTitle = {
      empty: "No API selected",
      needsMap: "Needs configuration",
      done: "Done",
      manual: "Manual (waits for webhook)",
    }[stage];

    const parallelCount = Array.isArray(node.post_triggers)
      ? node.post_triggers.length
      : 0;

    return (
      <li key={key}>
        <div
          className={`${S.treeRow} ${
            isNodeSelected ? S.treeRowSelected : ""
          } ${isEdgeHere ? S.treeRowEdgeActive : ""}`}
          onClick={() => setSelection({ kind: "node", nodePath })}
        >
          {branchKeys.length > 0 ? (
            <button
              className={S.treeRowChevronBtn}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(nodePath);
              }}
              title={expandedHere ? "Collapse" : "Expand"}
            >
              {expandedHere ? (
                <FaChevronDown size={10} />
              ) : (
                <FaChevronRight size={10} />
              )}
            </button>
          ) : (
            <span style={{ width: 14, display: "inline-block" }} />
          )}

          <span
            className={`${S.treeRowStageIcon} ${S[stage]}`}
            title={stageTitle}
            aria-label={stageTitle}
          >
            {stageGlyph}
          </span>

          {node.manual && (
            <span
              className={S.treeRowManualInline}
              title="Manual — pauses for external webhook"
            >
              <FaHandPaper size={9} /> Manual
            </span>
          )}

          {parallelCount > 0 && (
            <span
              title={`${parallelCount} parallel action${
                parallelCount === 1 ? "" : "s"
              } will fire when this API returns`}
              style={{
                fontSize: 10,
                color: "#5b7cfa",
                background: "#ebf0ff",
                border: "1px solid #dbe4ff",
                padding: "1px 6px",
                borderRadius: 10,
                fontWeight: 600,
              }}
            >
              ⚡ {parallelCount}
            </span>
          )}

          {!isRoot && (
            <span
              className={`${S.treeRowStatusPill} ${S[statusClass(nodePath[nodePath.length - 1])]}`}
            >
              {nodePath[nodePath.length - 1]}
            </span>
          )}

          {isRoot && <span className={S.treeRowStatusPill}>ROOT</span>}

          {method && <span className={S.treeRowMethod}>{method}</span>}

          <span
            className={`${S.treeRowLabel} ${isRoot ? S.treeRowLabelRoot : ""} ${!fullUrl ? S.treeRowUnset : ""} ${S.treeRowUrlFull}`}
            title={fullUrl || ""}
          >
            {displayUrl}
          </span>
        </div>

        {expandedHere && branchKeys.length > 0 && (
          <ul className={S.treeChildren}>
            {branchKeys.map((status) =>
              renderTreeNode(
                node.branches[status],
                [...nodePath, status],
                stageMap,
              ),
            )}
          </ul>
        )}
      </li>
    );
  };

  const renderTree = (stageMap) => (
    <div className={S.treePane}>
      <div className={S.treePaneTitle}>Chain Structure</div>
      <ul className={S.treeRoot}>{renderTreeNode(form.root, [], stageMap)}</ul>

      <div className={S.treeLegend}>
        <div className={S.treeLegendRow}>
          <span
            className={`${S.treeRowStageIcon} ${S.empty} ${S.treeLegendIcon}`}
          >
            !
          </span>
          <span>No API selected</span>
        </div>
        <div className={S.treeLegendRow}>
          <span
            className={`${S.treeRowStageIcon} ${S.needsMap} ${S.treeLegendIcon}`}
          >
            ✎
          </span>
          <span>Needs configuration</span>
        </div>
        <div className={S.treeLegendRow}>
          <span
            className={`${S.treeRowStageIcon} ${S.done} ${S.treeLegendIcon}`}
          >
            ✓
          </span>
          <span>Done</span>
        </div>
        <div className={S.treeLegendRow}>
          <span
            className={`${S.treeRowStageIcon} ${S.manual} ${S.treeLegendIcon}`}
          >
            ✋
          </span>
          <span>Manual (waits for webhook)</span>
        </div>
      </div>
    </div>
  );

  const renderProgressStrip = (a) => {
    const { totalNodes, nodesWithApi, totalEdges, edgesMapped } = a;
    const structureDone = nodesWithApi === totalNodes;
    const mappingDone = totalEdges === 0 || edgesMapped === totalEdges;

    const structureClass = structureDone
      ? "done"
      : nodesWithApi === 0
        ? "pending"
        : "partial";
    const mappingClass = mappingDone
      ? "done"
      : edgesMapped === 0
        ? "pending"
        : "partial";

    return (
      <div className={S.progressStrip}>
        <div className={S.progressPass}>
          <span className={S.progressPassLabel}>1. Pick APIs</span>
          <span className={`${S.progressPassCount} ${S[structureClass]}`}>
            {nodesWithApi}/{totalNodes}
          </span>
        </div>
        <span className={S.progressDivider}>→</span>
        <div className={S.progressPass}>
          <span className={S.progressPassLabel}>2. Configure mappings</span>
          <span className={`${S.progressPassCount} ${S[mappingClass]}`}>
            {edgesMapped}/{totalEdges}
          </span>
        </div>
        <span className={S.progressHint}>
          {structureDone && mappingDone
            ? "✓ Chain complete"
            : structureDone
              ? "Structure ready — now configure each branch mapping"
              : "Pick an API for every node first"}
        </span>
      </div>
    );
  };

  const renderBreadcrumb = () => {
    const nodePath = selection.nodePath;
    const crumbs = [{ label: "Root", path: [] }];
    nodePath.forEach((status, i) => {
      crumbs.push({
        label: status,
        path: nodePath.slice(0, i + 1),
      });
    });
    if (selection.kind === "edge") {
      crumbs.push({
        label: `→ ${selection.status}`,
        path: null,
      });
    }

    return (
      <div className={S.breadcrumb}>
        <span style={{ color: "#a0aec0", marginRight: 4 }}>Editing:</span>
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className={S.breadcrumbSep}>›</span>}
            {c.path === null || i === crumbs.length - 1 ? (
              <span
                className={`${S.breadcrumbCrumb} ${S.breadcrumbCrumbCurrent}`}
              >
                {c.label}
              </span>
            ) : (
              <button
                className={S.breadcrumbCrumb}
                onClick={() => setSelection({ kind: "node", nodePath: c.path })}
              >
                {c.label}
              </button>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderNodeDetail = () => {
    const nodePath = selection.nodePath;
    const node = selectedNode;
    if (!node) return null;

    const isRoot = nodePath.length === 0;
    const cfg = apiConfigs.find((c) => c.api_config_id === node.api_config_id);
    const responseStatuses = Object.keys(
      cfg?.api_config?.sample_responses_by_status || {},
    );
    const branchKeys = sortBranchKeys(Object.keys(node.branches || {}));
    const isManual = !!node.manual;
    const isNodeEmpty = !node.api_config_id;

    const nextIncomplete = findNextIncomplete(form.root, {
      kind: "node",
      nodePath,
    });

    const firstUnmappedChildStatus = branchKeys.find((status) => {
      const child = node.branches[status];
      if (!child?.api_config_id) return false;
      const entries =
        Object.keys(child.path_param_mappings || {}).length +
        Object.keys(child.query_param_mappings || {}).length +
        Object.keys(child.payload_param_mappings || {}).length +
        Object.keys(child.input_mapping || {}).length;
      return entries === 0;
    });

    const firstChildMissingApiStatus = branchKeys.find((status) => {
      const child = node.branches[status];
      return !child?.api_config_id;
    });

    const definitionsById = Object.fromEntries(
      connectorDefinitions.map((d) => [d.definition_id, d]),
    );

    const nodeSiteFields = siteFieldsForNode(nodePath);

    return (
      <>
        {renderBreadcrumb()}

        {isNodeEmpty && (
          <div className={`${S.infoCallout} ${S.info}`}>
            <span className={S.infoCalloutIcon}>💡</span>
            <div>
              <strong>Step 1 — pick this node's API.</strong> Once picked, the
              branch slots below are seeded from that API's declared response
              statuses. You'll wire mappings for each branch in step 2.
            </div>
          </div>
        )}

        <div className={S.detailSection}>
          <div className={S.detailSectionHeader}>
            {isRoot ? "Root API" : "API"}
          </div>
          <div
            className={`${S.detailNodeCard} ${
              isManual ? S.detailNodeCardManual : ""
            } ${isRoot ? S.detailNodeCardRoot : ""}`}
          >
            <div className={S.formGroup}>
              <label>
                {isRoot ? "Root API Configuration" : "API Configuration"}
              </label>
              <div className={S.selectWrapper}>
                <select
                  value={node.api_config_id}
                  onChange={(e) =>
                    handleNodeApiChange(nodePath, e.target.value)
                  }
                  className={S.select}
                >
                  <option value="">-- Select API --</option>
                  {apiConfigs.filter(
                    (c) => c.scope === "app" || !c.scope,
                  ).length > 0 && (
                    <optgroup label="App Level (this app)">
                      {apiConfigs
                        .filter((c) => c.scope === "app" || !c.scope)
                        .map((c) => {
                          const shortId =
                            c.api_config_id?.split("-").pop() || "";
                          return (
                            <option
                              key={c.api_config_id}
                              value={c.api_config_id}
                            >
                              {shortId} — {c.api_config?.url || c.api_config_id}{" "}
                              ({c.api_config?.method})
                            </option>
                          );
                        })}
                    </optgroup>
                  )}
                  {apiConfigs.filter((c) => c.scope === "tenant").length > 0 && (
                    <optgroup label="Shared (tenant-wide)">
                      {apiConfigs
                        .filter((c) => c.scope === "tenant")
                        .map((c) => {
                          const shortId =
                            c.api_config_id?.split("-").pop() || "";
                          return (
                            <option
                              key={c.api_config_id}
                              value={c.api_config_id}
                            >
                              {shortId} — {c.api_config?.url || c.api_config_id}{" "}
                              ({c.api_config?.method})
                            </option>
                          );
                        })}
                    </optgroup>
                  )}
                </select>
                <FaAngleDown className={S.selectIcon} />
              </div>
              {cfg && (
                <div className={S.selectedInfo}>
                  <span>{cfg.api_config?.url}</span>
                  <span className={S.methodTag}>{cfg.api_config?.method}</span>
                </div>
              )}
            </div>

            {node.api_config_id && (
              <TriggerAttachmentList
                attachments={node.post_triggers || []}
                definitionsById={definitionsById}
                siteFields={nodeSiteFields}
                operations={operations}
                onChange={(next) => {
                  setForm((prev) => ({
                    ...prev,
                    root: updateNodeAtPath(prev.root, nodePath, (n) => ({
                      ...n,
                      post_triggers: next,
                    })),
                  }));
                }}
                onAdd={() => openAttachFlow(nodePath)}
                readOnly={false}
              />
            )}

            <div
              className={S.formGroup}
              style={{ marginTop: "8px", marginBottom: "4px" }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  fontSize: "13px",
                }}
              >
                <input
                  type="checkbox"
                  checked={isManual}
                  onChange={(e) =>
                    handleNodeManualToggle(nodePath, e.target.checked)
                  }
                />
                <span>
                  <strong>Manual</strong>{" "}
                  <span style={{ color: "#718096", fontWeight: "normal" }}>
                    — pause here; the tenant fires the completion webhook
                  </span>
                </span>
              </label>
              {isManual && (
                <p
                  className={S.firstStepHint}
                  style={{
                    marginTop: "6px",
                    padding: "8px 10px",
                    backgroundColor: "#fff8e1",
                    border: "1px solid #ffd54f",
                    borderRadius: "4px",
                    fontSize: "12px",
                    color: "#5d4037",
                    lineHeight: 1.5,
                  }}
                >
                  Sageion will <strong>not call this API</strong>. The chain
                  pauses here and waits for your backend to fire{" "}
                  <code>/webhook/callback</code> with a <code>status_code</code>{" "}
                  and the completion data. That data becomes{" "}
                  <code>response_data</code> for the child branch below.
                </p>
              )}
            </div>

            {cfg && responseStatuses.length > 0 && !isManual && (
              <p className={S.firstStepHint}>
                Sample responses configured for statuses:{" "}
                {responseStatuses.join(", ")}
              </p>
            )}
          </div>

          {node.api_config_id && firstChildMissingApiStatus && (
            <div className={`${S.nodeNextBar} ${S.warn}`}>
              <span>
                <strong>Structure gap —</strong> the branch on status{" "}
                <code>{firstChildMissingApiStatus}</code> has no follow-up API
                yet.
              </span>
              <button
                className={S.nodeNextBtn}
                onClick={() =>
                  setSelection({
                    kind: "edge",
                    nodePath,
                    status: firstChildMissingApiStatus,
                  })
                }
              >
                Pick API →
              </button>
            </div>
          )}

          {node.api_config_id &&
            !firstChildMissingApiStatus &&
            firstUnmappedChildStatus && (
              <div className={S.nodeNextBar}>
                <span>
                  <strong>Step 2 —</strong> the branch on status{" "}
                  <code>{firstUnmappedChildStatus}</code> still needs its
                  mapping configured.
                </span>
                <button
                  className={S.nodeNextBtn}
                  onClick={() =>
                    setSelection({
                      kind: "edge",
                      nodePath,
                      status: firstUnmappedChildStatus,
                    })
                  }
                >
                  Configure mapping →
                </button>
              </div>
            )}

          {node.api_config_id &&
            !firstUnmappedChildStatus &&
            !firstChildMissingApiStatus &&
            nextIncomplete && (
              <div className={`${S.nodeNextBar} ${S.warn}`}>
                <span>This node is done. Next incomplete item:</span>
                <button
                  className={S.nodeNextBtn}
                  onClick={() => {
                    expandTo(
                      nextIncomplete.kind === "node"
                        ? nextIncomplete.nodePath
                        : nextIncomplete.nodePath,
                    );
                    setSelection(nextIncomplete);
                  }}
                >
                  Go to next →
                </button>
              </div>
            )}
        </div>

        {node.api_config_id && (
          <div className={S.detailSection}>
            <div className={S.detailSectionHeader}>
              {isRoot ? "Branches" : "Sub-branches"}{" "}
              {branchKeys.length > 0 && `(${branchKeys.length})`}
            </div>
            {branchKeys.length === 0 ? (
              <p className={S.firstStepHint}>
                This API declares no response statuses, so no branches were
                seeded. Change the API above to add branches.
              </p>
            ) : (
              <div className={S.branchList}>
                {branchKeys.map((status) => {
                  const child = node.branches[status];
                  const childCfg = apiConfigs.find(
                    (c) => c.api_config_id === child.api_config_id,
                  );
                  const childUrl = childCfg?.api_config?.url;
                  const childMethod = childCfg?.api_config?.method;

                  const mappingCount =
                    Object.keys(child.path_param_mappings || {}).length +
                    Object.keys(child.query_param_mappings || {}).length +
                    Object.keys(child.payload_param_mappings || {}).length;

                  const isEdgeSelected =
                    selection.kind === "edge" &&
                    selection.nodePath.join("/") === nodePath.join("/") &&
                    selection.status === status;

                  return (
                    <div
                      key={status}
                      className={`${S.branchRow} ${
                        isEdgeSelected ? S.branchRowSelected : ""
                      }`}
                      onClick={() =>
                        setSelection({ kind: "edge", nodePath, status })
                      }
                    >
                      <span
                        className={`${S.treeRowStatusPill} ${S[statusClass(status)]}`}
                      >
                        {status}
                      </span>
                      <div className={S.branchRowBody}>
                        <div className={S.branchRowTop}>
                          {childMethod && (
                            <span className={S.treeRowMethod}>
                              {childMethod}
                            </span>
                          )}
                          <span
                            className={`${S.branchRowUrl} ${
                              !childUrl ? S.branchRowUnset : ""
                            }`}
                          >
                            {childUrl || "No API selected"}
                          </span>
                          {child.manual && (
                            <span className={S.treeRowManualDot}>
                              <FaHandPaper size={9} /> Manual
                            </span>
                          )}
                        </div>
                        <div className={S.branchRowBottom}>
                          {child.api_config_id && mappingCount > 0 ? (
                            <span className={S.branchRowMappingBadge}>
                              ✓ {mappingCount} mapped
                            </span>
                          ) : child.api_config_id ? (
                            <span
                              className={`${S.branchRowMappingBadge} ${S.warn}`}
                            >
                              Needs mapping
                            </span>
                          ) : (
                            <span
                              className={S.branchRowUnset}
                              style={{ fontSize: 12 }}
                            >
                              Pick a follow-up API to configure
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        className={S.branchRowDeleteBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteBranch(nodePath, status);
                        }}
                        title="Delete this branch and everything below it"
                      >
                        <RiDeleteBin6Line size={15} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const renderEdgeDetail = () => {
    const { nodePath, status } = selection;
    const parent = getNodeAtPath(form.root, nodePath);
    if (!parent) return null;
    const child = parent.branches?.[status];
    if (!child) return null;

    const parentCfg = apiConfigs.find(
      (c) => c.api_config_id === parent.api_config_id,
    );
    const childCfg = apiConfigs.find(
      (c) => c.api_config_id === child.api_config_id,
    );

    const parentSampleResponseBody =
      parentCfg?.api_config?.sample_responses_by_status?.[status] ?? null;

    const leadFieldNames = getLeadFieldNames(nodePath);

    const mappingCount =
      Object.keys(child.path_param_mappings || {}).length +
      Object.keys(child.query_param_mappings || {}).length +
      Object.keys(child.payload_param_mappings || {}).length;

    const isMapped = mappingCount > 0;

    if (!child.api_config_id) {
      return (
        <>
          {renderBreadcrumb()}
          <button
            className={S.edgeBackBtn}
            onClick={() => setSelection({ kind: "node", nodePath })}
          >
            <FaChevronRight size={10} style={{ transform: "rotate(180deg)" }} />
            Back to parent API
          </button>

          <div className={S.edgeHeader}>
            <span
              className={`${S.treeRowStatusPill} ${S[statusClass(status)]}`}
            >
              {status}
            </span>
            <span className={S.edgeHeaderLabel}>leads to</span>
            <span className={S.edgeHeaderUrl} style={{ color: "#a0aec0" }}>
              No API selected yet
            </span>
          </div>

          <div className={`${S.infoCallout} ${S.info}`}>
            <span className={S.infoCalloutIcon}>💡</span>
            <div>
              <strong>Step 1 — pick the follow-up API for this branch.</strong>{" "}
              Once picked, its declared path / query / body parameters become
              available to map from this node's response.
            </div>
          </div>

          <div className={S.edgeMappingWrap}>
            <div className={S.formGroup}>
              <label>Follow-up API for status "{status}"</label>
              <div className={S.selectWrapper}>
                <select
                  value=""
                  onChange={(e) =>
                    handleNodeApiChange([...nodePath, status], e.target.value)
                  }
                  className={S.select}
                >
                  <option value="">-- Select API --</option>
                  {apiConfigs.filter(
                    (c) => c.scope === "app" || !c.scope,
                  ).length > 0 && (
                    <optgroup label="App Level (this app)">
                      {apiConfigs
                        .filter((c) => c.scope === "app" || !c.scope)
                        .map((c) => {
                          const shortId =
                            c.api_config_id?.split("-").pop() || "";
                          return (
                            <option
                              key={c.api_config_id}
                              value={c.api_config_id}
                            >
                              {shortId} — {c.api_config?.url || c.api_config_id}{" "}
                              ({c.api_config?.method})
                            </option>
                          );
                        })}
                    </optgroup>
                  )}
                  {apiConfigs.filter((c) => c.scope === "tenant").length > 0 && (
                    <optgroup label="Shared (tenant-wide)">
                      {apiConfigs
                        .filter((c) => c.scope === "tenant")
                        .map((c) => {
                          const shortId =
                            c.api_config_id?.split("-").pop() || "";
                          return (
                            <option
                              key={c.api_config_id}
                              value={c.api_config_id}
                            >
                              {shortId} — {c.api_config?.url || c.api_config_id}{" "}
                              ({c.api_config?.method})
                            </option>
                          );
                        })}
                    </optgroup>
                  )}
                </select>
                <FaAngleDown className={S.selectIcon} />
              </div>
            </div>
          </div>
        </>
      );
    }

    const nextIncomplete = findNextIncomplete(form.root, {
      kind: "edge",
      nodePath,
      status,
    });

    return (
      <>
        {renderBreadcrumb()}

        <button
          className={S.edgeBackBtn}
          onClick={() => setSelection({ kind: "node", nodePath })}
        >
          <FaChevronRight size={10} style={{ transform: "rotate(180deg)" }} />
          Back to parent API
        </button>

        <div className={S.edgeHeader}>
          <span className={`${S.treeRowStatusPill} ${S[statusClass(status)]}`}>
            {status}
          </span>
          <span className={S.edgeHeaderLabel}>leads to</span>
          {childCfg?.api_config?.method && (
            <span className={S.treeRowMethod}>
              {childCfg.api_config.method}
            </span>
          )}
          <span className={S.edgeHeaderUrl}>
            {childCfg?.api_config?.url || "No API selected"}
          </span>
          {child.manual && (
            <span className={S.treeRowManualDot}>
              <FaHandPaper size={9} /> Manual
            </span>
          )}
          {isMapped && (
            <span className={S.edgeDoneBadge} style={{ marginLeft: "auto" }}>
              ✓ {mappingCount} mapped
            </span>
          )}
        </div>

        {!isMapped && (
          <div className={`${S.infoCallout} ${S.warn}`}>
            <span className={S.infoCalloutIcon}>⚠️</span>
            <div>
              <strong>Step 2 — configure the mapping.</strong> Pick a source
              value for each parameter below, or use <strong>Auto-Map</strong>{" "}
              to match by name. When the summary at the top shows a green badge,
              this branch is done.
            </div>
          </div>
        )}

        <div className={S.edgeMappingWrap}>
          <StepMappingEditor
            stepIndex={nodePath.length}
            previousStepApiConfigId={parent.api_config_id}
            stepApiConfigId={child.api_config_id}
            mapping={child.input_mapping || {}}
            onMappingChange={(
              _stepIdx,
              statusCode,
              key,
              newKey,
              newValue,
              fullMapping,
            ) =>
              handleBranchMappingChange(
                nodePath,
                status,
                key,
                newKey,
                newValue,
                fullMapping,
              )
            }
            pathParamMappings={child.path_param_mappings || {}}
            queryParamMappings={child.query_param_mappings || {}}
            payloadParamMappings={child.payload_param_mappings || {}}
            onParamMappingsChange={(_stepIdx, _statusCode, payload) =>
              handleBranchParamMappingsChange(nodePath, status, payload)
            }
            apiConfigs={apiConfigs}
            statusCode={status}
            sampleResponseBody={parentSampleResponseBody}
            leadFieldNames={leadFieldNames}
          />
        </div>

        <div className={S.edgeNextBar}>
          <span className={S.edgeNextLabel}>
            {isMapped
              ? "This branch looks good."
              : "Add at least one mapping to mark this branch done."}
          </span>
          <button
            className={S.edgeNextBtn}
            disabled={!nextIncomplete}
            onClick={() => {
              if (!nextIncomplete) return;
              expandTo(
                nextIncomplete.kind === "node"
                  ? nextIncomplete.nodePath
                  : nextIncomplete.nodePath,
              );
              setSelection(nextIncomplete);
            }}
          >
            {nextIncomplete ? "Next incomplete →" : "All done ✓"}
          </button>
        </div>
      </>
    );
  };

  const renderDetail = () => {
    if (!showForm) return null;
    if (selection.kind === "edge") return renderEdgeDetail();
    return renderNodeDetail();
  };

  const renderChainCard = (chain) => {
    const root = chain.root || {};
    const rootCfg = apiConfigs.find(
      (c) => c.api_config_id === root.api_config_id,
    );
    const branchCount = countBranchesRecursive(root);
    const depth = maxDepth(root);

    const bucket = attachmentsByChain.get(chain.chain_id);
    const appNames = bucket?.app_names || [];
    const isAttachedByMe = attachedIds.has(chain.chain_id);
    const isAttachedByOthers = appNames.some((a) => a !== appName);
    const globalAdmin = isGlobalContext;

    let badgeKind;
    if (chain.scope === SCOPE.APP) {
      badgeKind = "app";
    } else if (globalAdmin) {
      badgeKind =
        appNames.length > 0 ? "tenant-detached-blocked" : "tenant-unattached";
    } else {
      badgeKind = isAttachedByMe ? "tenant-attached" : "tenant-available";
    }

    const canEdit = chain.scope === SCOPE.APP || globalAdmin;

    // ⭐ FIX: Also check if there is an app-scoped config that shadows this one.
    // (For chains, we just check if any app chain exists that shares the same name,
    // though technically shadowing is per-form/leadgen not per-chain.
    // However, for consistency with Auth, we allow attach/detach if globalAdmin or App owner.)
    const showAttachDetach =
      activeTab === SCOPE.TENANT && !globalAdmin && chain.scope === SCOPE.TENANT;

    return (
      <div key={chain.chain_id} className={S.chainCard}>
        <div className={S.chainCardHeader}>
          <div className={S.chainInfo}>
            <div style={{ marginBottom: 6 }}>
              <ResourceScopeBadge
                kind={badgeKind}
                count={
                  globalAdmin && appNames.length > 0
                    ? appNames.length
                    : undefined
                }
                onClick={
                  globalAdmin && appNames.length > 0
                    ? () =>
                        openGuardPopup({
                          refusal: {
                            kind: "attached-in-use",
                            apps: appNames,
                            refs: null,
                            message: `This shared chain is attached to ${appNames.length} app${appNames.length === 1 ? "" : "s"}: ${appNames.join(", ")}. Detach all apps before deleting.`,
                            canForce: false,
                            detail: "",
                            resourceId: chain.chain_id,
                          },
                        })
                    : undefined
                }
              />
            </div>
            <h3 className={S.chainName}>
              {chain.name || "Unnamed Chain"}
            </h3>
            <div className={S.badges}>
              <span className={S.stepBadge}>
                                Root: {rootCfg?.api_config?.url || "?"}
              </span>
              <span className={S.stepBadge}>
                {branchCount} branch{branchCount !== 1 ? "es" : ""}
              </span>
              {depth > 1 && (
                <span className={S.stepBadge}>{depth} levels deep</span>
              )}
              <span
                className={
                  chain.enabled !== false
                    ? S.enabledBadge
                    : S.disabledBadge
                }
              >
                {chain.enabled !== false ? "Enabled" : "Disabled"}
              </span>
            </div>

            {showAttachDetach && (
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {isAttachedByMe ? (
                  <button
                    type="button"
                    onClick={() => handleDetachChain(chain)}
                    style={{
                      padding: "6px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#c05621",
                      backgroundColor: "#fffaf0",
                      border: "1px solid #fbd38d",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <FaUnlink size={12} /> Detach from this app
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleAttachChain(chain)}
                    style={{
                      padding: "6px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#2b6cb0",
                      backgroundColor: "#ebf8ff",
                      border: "1px solid #90cdf4",
                      borderRadius: 6,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <FaLink size={12} /> Attach to this app
                  </button>
                )}
              </div>
            )}
          </div>
          <div className={S.actions}>
            {canEdit && (
              <button
                onClick={() => handleToggle(chain)}
                className={S.iconBtn}
              >
                {chain.enabled !== false ? (
                  <BiSolidToggleRight size={48} color="green" />
                ) : (
                  <BiSolidToggleLeft size={48} color="gray" />
                )}
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => openEdit(chain)}
                className={S.editButton}
                title={
                  chain.scope === SCOPE.TENANT && !globalAdmin
                    ? "You can attach/detach, but only the tenant admin can edit"
                    : "Modify this chain"
                }
              >
                <BiSolidEdit size={22} />
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => handleDelete(chain.chain_id)}
                className={S.deleteButton}
              >
                <RiDeleteBin6Line size={22} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const activeChains =
    activeTab === SCOPE.TENANT ? tenantChains : appChains;
  const tenantCount = tenantChains.length;
  const appCount = appChains.length;

  const hasAppOverride = appChains.length > 0;

  return (
    <div className={S.container}>
      <div className={S.header}>
        <div className={S.headerSection}>
          <h2 className={S.title}>API Chains</h2>
          <p className={S.subtitle}>
            Configure a branching API chain. Each API exposes its own set of
            response statuses, and the editor seeds a slot for each one. Wire
            the branches you need and save. Chains stop when a node has no
            matching branch for the status it returned. Mark any node{" "}
            <strong>Manual</strong> to skip the API call there and pause for an
            external webhook instead. Attach{" "}
            <strong>Parallel Actions</strong> to a node to fire connector
            operations in the background when its API returns — they never
            block the chain.
          </p>
        </div>
        <div className={S.headerActions}>
          <div className={S.viewToggle}>
            <button
              className={`${S.viewToggleBtn} ${viewMode === "list" ? S.active : ""}`}
              onClick={() => setViewMode("list")}
              title="List View"
            >
              <BiSolidGrid size={18} />
            </button>
            <button
              className={`${S.viewToggleBtn} ${viewMode === "canvas" ? S.active : ""}`}
              onClick={() => setViewMode("canvas")}
              title="Canvas View"
            >
              <BiSolidLayout size={18} />
            </button>
          </div>
          {activeTab === SCOPE.APP && !isGlobalContext && (
            <button onClick={openCreate} className={S.createButton}>
              + Create Chain
            </button>
          )}
          {activeTab === SCOPE.TENANT && !isGlobalContext && (
            <button
              type="button"
              className={S.createButton}
              disabled
              title="Tenant-level chains are created from the Global Level Settings surface, not from an app's ChainApis tab."
              style={{ opacity: 0.5, cursor: "not-allowed" }}
            >
              + Create Chain
            </button>
          )}
          {activeTab === SCOPE.TENANT && isGlobalContext && (
            <button
              type="button"
              className={S.createButton}
              onClick={openCreate}
            >
              + Create Chain
            </button>
          )}
        </div>
      </div>

      <ScopeTabs
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          if (showForm) closeForm();
        }}
        tenantCount={tenantCount}
        appCount={appCount}
        resourceNoun="chain"
        overriddenByAppLevel={
          activeTab === SCOPE.TENANT && hasAppOverride && !isGlobalContext
        }
        attachCount={attachedIds.size}
        disabled={loading}
        compact={false}
        hiddenTabs={isGlobalContext ? ["app"] : []}
      />

      {/* ⭐ Reload button per tab */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 12,
        }}
      >
        <button
          onClick={fetchChains}
          disabled={loading}
          title="Reload chains"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: 12,
            color: "#475569",
            opacity: loading ? 0.6 : 1,
          }}
        >
          <FaSyncAlt size={11} /> Reload
        </button>
      </div>

      {viewMode === "canvas" && activeTab === SCOPE.APP && !isGlobalContext ? (
        <CanvasView
          chains={appChains}
          apiConfigs={apiConfigs}
          onEdit={openEdit}
          onDelete={handleDelete}
          onToggle={handleToggle}
          onSave={async (payload) => {
            try {
              const resp = await fetch(
                `https://${region}.autobot2.${backend_api_base_url}/chain`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                  },
                  body: JSON.stringify(payload),
                },
              );
              if (resp.ok) {
                openAlert({
                  heading: "Chain saved",
                  paragraph: "Chain saved successfully.",
                  icon: <FaCheckCircle size={28} />,
                  iconColor: "#16a34a",
                  iconBG: "#dcfce7",
                });
                fetchChains();
                return Promise.resolve();
              } else {
                const err = await resp.json();
                openAlert({
                  heading: "Failed to save",
                  paragraph: err.error,
                  icon: <FaTimesCircle size={28} />,
                  iconColor: "#dc2626",
                  iconBG: "#fee2e2",
                });
                return Promise.reject(err);
              }
            } catch (e) {
              openAlert({
                heading: "Error saving chain",
                paragraph: e.message || "Something went wrong.",
                icon: <FaTimesCircle size={28} />,
                iconColor: "#dc2626",
                iconBG: "#fee2e2",
              });
              return Promise.reject(e);
            }
          }}
          tenantId={appData.tenant_id}
          appName={appName}
        />
      ) : (
        <>
          {activeChains.length === 0 && !showForm && (
            <div className={S.emptyState}>
              {activeTab === SCOPE.TENANT ? (
                <p>
                  {isGlobalContext
                    ? "No shared chains yet. Create one to make it available to every app on this tenant."
                    : "Your tenant admin hasn't published any shared chains yet. Create your own from the App Level tab."}
                </p>
              ) : (
                <p>No app-scoped chains yet. Create your first chain above.</p>
              )}
            </div>
          )}

          <div className={S.list}>
            {activeChains.map((chain) => renderChainCard(chain))}
          </div>
        </>
      )}

      {showForm && (
        <div className={S.overlay}>
          <div
            className={`${S.modal} ${S.modalWide}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={S.modalHeaderBar}>
              <div className={S.modalHeaderTop}>
                <h3 className={S.modalHeaderTitle}>
                  {editingChainId ? "Edit Chain" : "New Chain"}
                </h3>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Chain name"
                  className={S.chainNameInput}
                />
                <label className={S.chainEnabledToggle}>
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        enabled: e.target.checked,
                      }))
                    }
                  />
                  {form.enabled ? "Enabled" : "Disabled"}
                </label>
                <div className={S.modalHeaderSpacer} />
                <button onClick={closeForm} className={S.cancelBtn}>
                  Cancel
                </button>
                <button onClick={handleSave} className={S.saveBtn}>
                  Save Chain
                </button>
              </div>
            </div>

            <div className={S.modalBodySplit}>
              {renderTree(analysis.perNodeStage)}
              <div className={S.detailPane}>
                {renderProgressStrip(analysis)}
                {renderDetail()}
              </div>
            </div>
          </div>
        </div>
      )}

      <TriggerAttachFlow
        isOpen={attachFlowOpen}
        onClose={closeAttachFlow}
        siteFields={
          attachFlowTargetNodePath
            ? siteFieldsForNode(attachFlowTargetNodePath)
            : []
        }
        connectedProviders={connectedProviders}
        definitions={connectorDefinitions}
        operations={operations}
        existingAttachments={
          attachFlowTargetNodePath
            ? getNodeAtPath(form.root, attachFlowTargetNodePath)
                ?.post_triggers || []
            : []
        }
        onAttach={handleAttachTrigger}
      />

      <ReusablePopup
        isOpen={popup.isOpen}
        onClose={closePopup}
        onConfirm={popup.onConfirm}
        icon={popup.icon}
        iconColor={popup.iconColor}
        iconBG={popup.iconBG}
        heading={popup.heading}
        subtext={popup.subtext}
        paragraph={popup.paragraph}
        cancelText={popup.cancelText}
        confirmText={popup.confirmText}
        showCancel={!!popup.cancelText}
        showConfirm={true}
      />
    </div>
  );
}